import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { componentInstanceLevelId } from "../generationTools/components/componentInstanceLevel";

interface EvaluatedComponentRoomParentContext {
  readonly rawBlock: Block;
  readonly componentBlock: Block;
  readonly componentRoomLevel: Level | undefined;
}

export function evaluateComponentRoomParentContext(
  room: Building["doc"]["rooms"][string],
  componentTransform: ComponentTransform,
  rawBlocks: Building["doc"]["blocks"],
  rawLevels: Building["doc"]["levels"],
  levels: Building["levels"],
  definitions: OptionDefinitions,
  blockOptionChoices: ReturnType<typeof resolveOptionsInFormula>,
  levelOptionChoices: ReturnType<typeof resolveOptionsInFormula>,
  entityDisabledByGroup: Building["entityDisabledByGroup"],
): EvaluatedComponentRoomParentContext | undefined {
  const { componentInstanceChain, verticalPlacement } = componentTransform;

  const rawBlock = rawBlocks[room.parentId];
  if (!rawBlock) {
    return;
  }

  const rawLevel = rawLevels[rawBlock.levelId];
  let componentRoomLevel: Level | undefined;

  if (rawLevel) {
    const componentLevel = evaluateEntityForComponentInstance(
      rawLevel,
      componentInstanceChain,
      definitions,
      levelOptionChoices,
    );
    if (componentLevel.disabled) {
      return;
    }

    const componentRoomLevelId = componentInstanceLevelId(rawLevel.id, levels, verticalPlacement, [
      ...componentInstanceChain,
    ]);
    componentRoomLevel = levels[componentRoomLevelId] ?? levels[rawLevel.id];
    if (!componentRoomLevel) {
      return;
    }
  }

  const componentBlock = evaluateEntityForComponentInstance(
    rawBlock,
    componentInstanceChain,
    definitions,
    blockOptionChoices,
  );
  if (componentBlock.disabled || entityDisabledByGroup[componentBlock.id]) {
    return;
  }

  return {
    rawBlock,
    componentBlock,
    componentRoomLevel,
  };
}
